/** Typed transport for the current organization finance journey. */
import { httpClient } from "./client";
import type {
  ConnectedAccountProvisioningResponse,
  FinanceAccountRefreshResponse,
  FinanceContextResponse,
  FinanceDisputePage,
  FinanceDisputeResponse,
  FinanceEmbeddedSessionRequest,
  FinanceEmbeddedSessionResponse,
  OrgFinanceAccess,
  OrgFinanceGrantReplace,
  PayoutPullStartRequest,
  PayoutPullAdvanceRequest,
  PayoutPullResponse,
  Schema,
} from "../types/generated";
import type { paths } from "../types/generated/api";

export type FinanceDisputeListParams = NonNullable<
  paths["/orgs/{org_id}/finance/disputes"]["get"]["parameters"]["query"]
>;
export type FinanceSettlementParams = NonNullable<
  paths["/analytics/orgs/{org_id}/settlements"]["get"]["parameters"]["query"]
>;
export type FinancePayoutParams = NonNullable<
  paths["/analytics/orgs/{org_id}/payouts"]["get"]["parameters"]["query"]
>;
export type FinanceReconciliationParams = NonNullable<
  paths["/analytics/orgs/{org_id}/reconciliation"]["get"]["parameters"]["query"]
>;

/** Backend owns authority, lifecycle, account binding, readiness and arithmetic. */
export interface FinanceApi {
  getContext(orgId: string, signal?: AbortSignal): Promise<FinanceContextResponse>;
  getProvisioning(orgId: string, signal?: AbortSignal): Promise<ConnectedAccountProvisioningResponse | null>;
  ensureProvisioning(orgId: string): Promise<ConnectedAccountProvisioningResponse>;
  createOnboardingSession(orgId: string): Promise<FinanceEmbeddedSessionResponse>;
  createAccountSession(orgId: string, request: FinanceEmbeddedSessionRequest): Promise<FinanceEmbeddedSessionResponse>;
  refreshAccount(orgId: string): Promise<FinanceAccountRefreshResponse>;
  listDisputes(orgId: string, params?: FinanceDisputeListParams, signal?: AbortSignal): Promise<FinanceDisputePage>;
  getDispute(orgId: string, disputeId: string, signal?: AbortSignal): Promise<FinanceDisputeResponse>;
  getSettlements(orgId: string, params?: FinanceSettlementParams, signal?: AbortSignal): Promise<Schema["SettlementSummaryResponse"]>;
  getPayouts(orgId: string, params?: FinancePayoutParams, signal?: AbortSignal): Promise<Schema["PayoutHistoryResponse"]>;
  getReconciliation(orgId: string, params?: FinanceReconciliationParams, signal?: AbortSignal): Promise<Schema["ReconciliationResponse"]>;
  getTeamMemberFinanceAccess(orgId: string, memberId: string, signal?: AbortSignal): Promise<OrgFinanceAccess>;
  replaceTeamMemberFinanceAccess(orgId: string, memberId: string, request: OrgFinanceGrantReplace): Promise<OrgFinanceAccess>;
  startPayoutPull(orgId: string, request: PayoutPullStartRequest): Promise<PayoutPullResponse>;
  getPayoutPull(orgId: string, runId: string, signal?: AbortSignal): Promise<PayoutPullResponse>;
  advancePayoutPull(orgId: string, runId: string, request: PayoutPullAdvanceRequest): Promise<PayoutPullResponse>;
}

function query(values: object | undefined): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values ?? {})) {
    if (value !== undefined && value !== null) params.set(key, String(value));
  }
  return params.size ? `?${params.toString()}` : "";
}

const organization = (id: string) => `/orgs/${encodeURIComponent(id)}`;
const reports = (id: string) => `/analytics/orgs/${encodeURIComponent(id)}`;
const member = (orgId: string, memberId: string) =>
  `/staff/team-members/${encodeURIComponent(memberId)}/finance-access${query({ org_id: orgId })}`;

export const financeApi: FinanceApi = {
  getContext: (orgId, signal) => httpClient.get(`${organization(orgId)}/finance/context`, { signal, cache: "no-store" }),
  getProvisioning: (orgId, signal) => httpClient.get(`${organization(orgId)}/setup/payment-account-provisioning`, { signal, cache: "no-store" }),
  ensureProvisioning: (orgId) => httpClient.post(`${organization(orgId)}/setup/payment-account-provisioning`, {}),
  createOnboardingSession: (orgId) => httpClient.post(`${organization(orgId)}/setup/stripe-account-session`, {}),
  createAccountSession: (orgId, request) => httpClient.post(`${organization(orgId)}/finance/account-session`, request),
  refreshAccount: (orgId) => httpClient.post(`${organization(orgId)}/finance/account-refresh`, {}),
  listDisputes: (orgId, params, signal) => httpClient.get(`${organization(orgId)}/finance/disputes${query(params)}`, { signal, cache: "no-store" }),
  getDispute: (orgId, disputeId, signal) => httpClient.get(`${organization(orgId)}/finance/disputes/${encodeURIComponent(disputeId)}`, { signal, cache: "no-store" }),
  getSettlements: (orgId, params, signal) => httpClient.get(`${reports(orgId)}/settlements${query(params)}`, { signal, cache: "no-store" }),
  getPayouts: (orgId, params, signal) => httpClient.get(`${reports(orgId)}/payouts${query(params)}`, { signal, cache: "no-store" }),
  getReconciliation: (orgId, params, signal) => httpClient.get(`${reports(orgId)}/reconciliation${query(params)}`, { signal, cache: "no-store" }),
  getTeamMemberFinanceAccess: (orgId, memberId, signal) => httpClient.get(member(orgId, memberId), { signal, cache: "no-store" }),
  replaceTeamMemberFinanceAccess: (orgId, memberId, request) => httpClient.put(member(orgId, memberId), request),
  startPayoutPull: (orgId, request) => httpClient.post(`${organization(orgId)}/finance/payout-pulls`, request),
  getPayoutPull: (orgId, runId, signal) => httpClient.get(`${organization(orgId)}/finance/payout-pulls/${encodeURIComponent(runId)}`, { signal, cache: "no-store" }),
  advancePayoutPull: (orgId, runId, request) => httpClient.post(`${organization(orgId)}/finance/payout-pulls/${encodeURIComponent(runId)}/advance`, request),
};
